import { useEffect, useMemo, useState } from "react";
import { History, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { QueryState } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EDITABLE } from "@/components/DraftTools";

const MAX_TOKENS = 2500;

// Word-level LCS diff; returns [{t, kind: "same"|"add"|"del"}]
export function diffWords(a = "", b = "") {
  const x = a.split(/(\s+)/); const y = b.split(/(\s+)/);
  if (x.length > MAX_TOKENS || y.length > MAX_TOKENS) return null;
  const m = x.length; const n = y.length;
  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = []; let i = 0; let j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) { out.push({ t: x[i], kind: "same" }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ t: x[i++], kind: "del" });
    else out.push({ t: y[j++], kind: "add" });
  }
  while (i < m) out.push({ t: x[i++], kind: "del" });
  while (j < n) out.push({ t: y[j++], kind: "add" });
  return out;
}

function Side({ title, label, tokens, show, testId, fallback }) {
  return (
    <div data-testid={testId} className="min-w-0 rounded-lg border border-[#172342] bg-[#050816]/50 p-4">
      <div className="cq-muted-label mb-2">{label}</div>
      <div className="text-sm font-medium text-[#F7FAFF] mb-2">{title}</div>
      <p className="text-[13px] leading-relaxed text-[#D5DDEB] whitespace-pre-wrap break-words">
        {tokens ? tokens.filter((tk) => tk.kind === "same" || tk.kind === show).map((tk, k) => (tk.kind === "same" ? <span key={k}>{tk.t}</span>
          : <mark key={k} className={tk.kind === "add" ? "bg-[#4BE3A4]/20 text-[#B9F5DA] rounded-sm" : "bg-[#FF7792]/20 text-[#FFC2CF] line-through decoration-[#FF7792]/60 rounded-sm"}>{tk.t}</mark>)) : fallback}
      </p>
    </div>
  );
}

export default function VersionCompare({ item, onClose }) {
  const { path, can, refresh } = useWorkspace();
  const q = useWsQuery(`/r/content_items/${item?.id}/versions`, { enabled: !!item });
  const [sel, setSel] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!q.data?.some((x) => x.id === sel)) setSel(q.data?.[0]?.id || null); }, [q.data, sel]);
  const v = (q.data || []).find((x) => x.id === sel);
  const tokens = useMemo(() => (v && item ? diffWords(v.body || "", item.body || "") : null), [v, item]);
  const stats = tokens && { add: tokens.filter((t) => t.kind === "add" && t.t.trim()).length, del: tokens.filter((t) => t.kind === "del" && t.t.trim()).length };
  const canRestore = item && can("edit") && EDITABLE.includes(item.status || "draft");
  const restore = async () => {
    setBusy(true);
    try { await api.post(path(`/content/${item.id}/versions/${v.id}/restore`)); toast.success(`Restored v${v.version}. The replaced text is saved in history.`); refresh(); const r = await q.refetch(); setSel(r.data?.[0]?.id || null); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="content-versions-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF] max-w-6xl w-[96vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><History size={16} className="text-[#22E6F2]" /> Compare versions · {item?.title}</DialogTitle></DialogHeader>
        <QueryState q={q} empty={<p className="text-sm text-[#AAB6CC]">No earlier versions. Edits, rewrites and restores create versions automatically.</p>}>
          {(vs) => (
            <div className="grid lg:grid-cols-[220px_1fr] gap-5">
              <div className="space-y-1.5 lg:max-h-[65vh] lg:overflow-y-auto" data-testid="version-list">
                {vs.map((x) => (
                  <button key={x.id} data-testid={`version-item-${x.version}`} onClick={() => setSel(x.id)}
                    className={`w-full text-left p-2.5 rounded-lg border transition-colors ${sel === x.id ? "border-[#22E6F2]/50 bg-[#22E6F2]/5" : "border-[#172342] hover:border-[#3A4870]"}`}>
                    <div className="text-xs font-mono text-[#22E6F2]">v{x.version}</div>
                    <div className="text-[11px] text-[#7C8BAD]">{fmtDateTime(x.created_at)}</div>
                    {x.note && <div className="text-[11px] text-[#B89CFF] mt-0.5 line-clamp-2">{x.note}</div>}
                  </button>))}
              </div>
              {v && (
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
                    {stats ? <span data-testid="version-diff-stats" className="font-mono"><span className="text-[#4BE3A4]">+{stats.add} words</span> · <span className="text-[#FF7792]">−{stats.del} words</span></span>
                      : <span className="text-[#7C8BAD]">Too long to highlight changes; showing both texts.</span>}
                    {v.title !== item.title && <span className="text-[#F4B860]">Title changed</span>}
                    <span className="ml-auto" />
                    {canRestore ? <button data-testid="version-restore-button" disabled={busy} onClick={() => setConfirm(true)} className="cq-btn cq-btn-ghost !py-1.5 !text-xs">
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Restore v{v.version}</button>
                      : <span data-testid="version-restore-locked" className="text-[#7C8BAD]">{can("edit") ? "Restore is locked while in review or approved" : "View only"}</span>}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Side testId="version-side-old" label={`v${v.version} · earlier`} title={v.title} tokens={tokens} show="del" fallback={v.body} />
                    <Side testId="version-side-current" label={`v${item.version || 1} · current`} title={item.title} tokens={tokens} show="add" fallback={item.body} />
                  </div>
                </div>)}
            </div>)}
        </QueryState>
        <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={`Restore v${v?.version}?`} confirmLabel="Restore"
          description="The current text is saved in history first. The draft returns to Draft status and any open review is cancelled." onConfirm={restore} />
      </DialogContent>
    </Dialog>
  );
}
